import React, { useRef, useState } from "react";
import { FileUp, X, Loader2, FileText, Eye } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * PdfUploader — supports both single (string) and multi (string[]) modes.
 *
 * Props:
 *   value      : string | string[]  — current PDF url(s)
 *   onChange   : (next) => void     — same shape as value
 *   multiple   : boolean            — default true
 *   maxMb      : number             — client-side size guard, default 50
 *   onPreview  : (url) => void      — optional; called when user taps preview.
 *                                     If not provided, opens PDF in a new tab.
 */
export default function PdfUploader({ value, onChange, multiple = true, maxMb = 50, onPreview }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  // Normalise to array internally.
  const list = Array.isArray(value) ? value : (value ? [value] : []);
  const emitArray = Array.isArray(value) || multiple;

  const push = (next) => onChange(emitArray ? next : (next[0] || ""));

  const openPreview = (url) => {
    if (typeof onPreview === "function") return onPreview(url);
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  const uploadOne = async (file) => {
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      toast.error(`"${file.name}" is not a PDF.`);
      return null;
    }
    if (file.size > maxMb * 1024 * 1024) {
      toast.error(`"${file.name}" is larger than ${maxMb} MB.`);
      return null;
    }
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/upload", fd, {
      onUploadProgress: (evt) => {
        if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
      },
    });
    return data?.url || null;
  };

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    const arr = Array.from(files);
    setUploading(true);
    setProgress(0);
    const uploaded = [];
    try {
      for (const f of arr) {
        try {
          const url = await uploadOne(f);
          if (url) uploaded.push(url);
        } catch (err) {
          const detail = err?.response?.data?.detail;
          toast.error(typeof detail === "string" ? detail : `Failed to upload "${f.name}"`);
        }
      }
      if (uploaded.length > 0) {
        push(multiple ? [...list, ...uploaded] : uploaded);
        toast.success(uploaded.length === 1 ? "PDF uploaded" : `${uploaded.length} PDFs uploaded`);
      }
    } finally {
      setUploading(false);
      setProgress(0);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removeAt = (idx) => {
    const next = list.filter((_, i) => i !== idx);
    push(next);
  };

  return (
    <div data-testid="pdf-uploader">
      {list.length > 0 && (
        <div className="space-y-2 mb-3">
          {list.map((url, i) => {
            const displayName = decodeURIComponent((url || "").split("/").pop() || `Document ${i + 1}`).slice(-42);
            return (
              <div key={`${url}-${i}`} className="flex items-center gap-3 rounded-lg border border-border bg-white p-2.5" data-testid={`pdf-item-${i}`}>
                <FileText className="h-6 w-6 text-primary shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-ink truncate">{displayName}</p>
                  <p className="text-[11px] text-muted-foreground">PDF attachment</p>
                </div>
                <button
                  type="button"
                  onClick={() => openPreview(url)}
                  className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                  data-testid={`pdf-preview-${i}`}
                >
                  <Eye className="h-3.5 w-3.5" /> Preview
                </button>
                <button
                  type="button"
                  onClick={() => removeAt(i)}
                  className="h-7 w-7 rounded-full border border-border text-rose-600 hover:bg-rose-50 flex items-center justify-center"
                  aria-label="Remove PDF"
                  data-testid={`pdf-remove-${i}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {(multiple || list.length === 0) && (
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
          onClick={() => inputRef.current?.click()}
          className="border-2 border-dashed border-border rounded-2xl p-6 text-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-all"
          data-testid="pdf-dropzone"
        >
          {uploading ? (
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="h-7 w-7 animate-spin text-primary" />
              <p className="text-xs text-muted-foreground">Uploading… {progress}%</p>
            </div>
          ) : (
            <>
              <FileUp className="h-7 w-7 mx-auto text-primary" />
              <p className="mt-2 text-sm font-medium text-ink">
                {list.length > 0 ? "Add more PDFs" : "Upload PDF (Optional)"}
              </p>
              <p className="text-xs text-muted-foreground">Drag &amp; drop or tap · PDF only · max {maxMb} MB{multiple ? " each" : ""}</p>
            </>
          )}
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,.pdf"
            multiple={multiple}
            hidden
            onChange={(e) => handleFiles(e.target.files)}
            data-testid="pdf-input"
          />
        </div>
      )}
    </div>
  );
}
